import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";

export interface KiteBrand {
  id: number;
  brand: string;
  modelsCount?: number;
}

export interface KiteBrandPaginatedResponse {
  data: KiteBrand[];
  totalPages: number;
  totalBrands: number;
  totalItems: number; // Alias para totalBrands para compatibilidad con componente genérico
  currentPage: number;
}

export interface KiteBrandRequestDTO {
  brand: string;
}

export const KiteBrandService = {
  /**
   * Get brands with pagination, sorting and search capabilities
   * @param limit - Maximum number of results per page
   * @param sortField - Field to sort by
   * @param sortOrder - Sort direction (asc or desc)
   * @param page - Page number to fetch
   * @param searchValue - Optional search term
   * @returns Paginated brands response
   */
  async getBrands(
    limit: number = 5,
    sortField: string = "brand",
    sortOrder: string = "asc",
    page: number = 1,
    searchValue?: string
  ): Promise<KiteBrandPaginatedResponse> {
    return requestHandler<KiteBrandPaginatedResponse>(async () => {
      let url = `${ENDPOINTS.GEAR.BRANDS_PAGINATED}?limit=${limit}&sortField=${sortField}&sortOrder=${sortOrder}&page=${page}`;

      if (searchValue) {
        url += `&searchval=${encodeURIComponent(searchValue)}`;
      }

      const response = await axiosInstance.get(url);
      const data = response.data;

      // Asignar totalItems como alias de totalBrands para compatibilidad con el componente genérico
      if (data && typeof data.totalBrands === "number") {
        data.totalItems = data.totalBrands;
      }

      return data;
    }, "KiteBrandService -> getBrands");
  },

  /**
   * Search for brands by name
   * @param searchValue - The search term (minimum 3 characters)
   * @param limit - Maximum number of results to return
   * @returns Array of matching brands
   */
  async searchBrands(
    searchValue: string,
    limit: number = 10
  ): Promise<KiteBrand[]> {
    // Only perform the search if there are at least 3 characters
    if (searchValue.length < 3) {
      return [];
    }

    return requestHandler<KiteBrand[]>(async () => {
      const response = await axiosInstance.get(
        `${ENDPOINTS.GEAR.BRANDS_PAGINATED}?searchval=${encodeURIComponent(
          searchValue
        )}&limit=${limit}&sortField=brand&sortOrder=asc`
      );
      return response.data?.data || [];
    }, "KiteBrandService -> searchBrands");
  },

  /**
   * Fetch a brand by its ID
   * @param brandId - The ID of the brand to fetch
   * @returns The brand or null if not found
   */
  async getBrandById(brandId: number): Promise<KiteBrand | null> {
    return requestHandler<KiteBrand | null>(async () => {
      const response = await axiosInstance.get(
        `${ENDPOINTS.GEAR.BRANDS}/${brandId}`
      );
      return response.data || null;
    }, "KiteBrandService -> getBrandById");
  },

  /**
   * Create a new brand
   * @param brandData - The brand data to create
   * @returns The created brand or null if creation failed
   */
  async createBrand(brandData: KiteBrandRequestDTO): Promise<KiteBrand | null> {
    return requestHandler<KiteBrand | null>(async () => {
      const response = await axiosInstance.post(
        ENDPOINTS.GEAR.BRANDS,
        brandData
      );
      return response.data || null;
    }, "KiteBrandService -> createBrand");
  },

  /**
   * Update an existing brand
   * @param brandId - The ID of the brand to update
   * @param brandData - The updated brand data
   * @returns The updated brand or null if update failed
   */
  async updateBrand(
    brandId: number,
    brandData: KiteBrandRequestDTO
  ): Promise<KiteBrand | null> {
    return requestHandler<KiteBrand | null>(async () => {
      const response = await axiosInstance.put(
        `${ENDPOINTS.GEAR.BRANDS_UPDATE(brandId)}`,
        brandData
      );
      return response.data || null;
    }, "KiteBrandService -> updateBrand");
  },

  /**
   * Delete a brand
   * @param brandId - The ID of the brand to delete
   * @returns True if deletion was successful, false otherwise
   */
  async deleteBrand(brandId: number): Promise<boolean> {
    return requestHandler<boolean>(async () => {
      const response = await axiosInstance.delete(
        `${ENDPOINTS.GEAR.BRANDS_DELETE(brandId)}`
      );
      return response.status >= 200 && response.status < 300;
    }, "KiteBrandService -> deleteBrand");
  },
};
